/** Apply a sparse edit patch to a person, returning a new object. */
function withEdits(person, scenario) {
    const edit = scenario?.edits[person.id];
    if (!edit)
        return person;
    const next = { ...person };
    if ('managerId' in edit)
        next.managerId = edit.managerId ?? null;
    if ('title' in edit && edit.title !== undefined)
        next.title = edit.title;
    if ('department' in edit)
        next.department = edit.department ?? null;
    if ('name' in edit && edit.name !== undefined)
        next.name = edit.name;
    if ('location' in edit)
        next.location = edit.location ?? null;
    if ('startDate' in edit)
        next.startDate = edit.startDate ?? null;
    if ('plannedCost' in edit)
        next.plannedCost = edit.plannedCost ?? null;
    if ('plannedReason' in edit)
        next.plannedReason = edit.plannedReason ?? null;
    if ('notes' in edit)
        next.notes = edit.notes ?? null;
    if ('dottedManagerIds' in edit)
        next.dottedManagerIds = edit.dottedManagerIds ?? [];
    return next;
}
/**
 * Build the effective, fully-derived org from a snapshot plus an optional
 * scenario. This is the single place the tree is assembled — the web app, the
 * API server and the MCP server all call it, so they can never disagree about
 * what a scenario means.
 *
 * Defensive by design: bad manager pointers and cycles (which are possible in
 * a hand-edited or stale imported scenario file) are repaired and reported in
 * `warnings` rather than throwing or hanging the UI.
 */
export function buildOrgModel(snapshot, scenario) {
    const warnings = [];
    const baseById = new Map();
    for (const person of snapshot.people)
        baseById.set(person.id, person);
    const byId = new Map();
    for (const person of snapshot.people)
        byId.set(person.id, withEdits(person, scenario));
    for (const hire of scenario?.newPeople ?? []) {
        if (byId.has(hire.id)) {
            warnings.push(`Planned hire "${hire.name}" collides with an existing person id and was skipped.`);
            continue;
        }
        byId.set(hire.id, withEdits({ ...hire, isNew: true, status: 'Planned' }, scenario));
    }
    // Repair manager pointers that don't resolve.
    for (const person of byId.values()) {
        if (person.managerId !== null && !byId.has(person.managerId)) {
            warnings.push(`${person.name} pointed at a manager who is not in this org (${person.managerId}); treated as unassigned.`);
            person.managerId = null;
        }
        if (person.managerId === person.id) {
            warnings.push(`${person.name} was set as their own manager; treated as unassigned.`);
            person.managerId = null;
        }
    }
    const childIds = new Map();
    const collectChildren = () => {
        childIds.clear();
        for (const person of byId.values()) {
            if (person.managerId === null)
                continue;
            const siblings = childIds.get(person.managerId);
            if (siblings)
                siblings.push(person.id);
            else
                childIds.set(person.managerId, [person.id]);
        }
    };
    collectChildren();
    let rootIds = [...byId.values()].filter((p) => p.managerId === null).map((p) => p.id);
    // Break any cycles: anything unreachable from a root is, by definition, in one.
    for (let guard = 0; guard < 50; guard += 1) {
        const reachable = new Set();
        const stack = [...rootIds];
        while (stack.length > 0) {
            const id = stack.pop();
            if (reachable.has(id))
                continue;
            reachable.add(id);
            for (const child of childIds.get(id) ?? [])
                stack.push(child);
        }
        if (reachable.size === byId.size)
            break;
        const stranded = [...byId.keys()].filter((id) => !reachable.has(id)).sort();
        const promoteId = stranded[0];
        if (promoteId === undefined)
            break;
        const promoted = byId.get(promoteId);
        warnings.push(`Reporting loop detected around ${promoted.name}; their manager link was cleared to keep the chart usable.`);
        promoted.managerId = null;
        collectChildren();
        rootIds = [...byId.values()].filter((p) => p.managerId === null).map((p) => p.id);
    }
    const { counts, depth, order } = computeCountsAndDepth(rootIds, childIds);
    // Order siblings: biggest teams first, then alphabetically. Deterministic, and
    // it keeps managers visually grouped to the left of individual contributors.
    const sortSiblings = (ids) => ids.sort((a, b) => {
        const ca = counts.get(a)?.totalReports ?? 0;
        const cb = counts.get(b)?.totalReports ?? 0;
        if (ca !== cb)
            return cb - ca;
        const na = byId.get(a)?.name ?? '';
        const nb = byId.get(b)?.name ?? '';
        return na.localeCompare(nb);
    });
    for (const ids of childIds.values())
        sortSiblings(ids);
    sortSiblings(rootIds);
    const departures = new Set((scenario?.departures ?? []).filter((id) => byId.has(id)));
    return {
        people: order.map((id) => byId.get(id)),
        byId,
        baseById,
        childIds,
        rootIds,
        counts,
        depth,
        departures,
        warnings,
    };
}
/**
 * Iterative post-order walk. Iterative rather than recursive so a pathological
 * imported scenario can never blow the stack.
 */
function computeCountsAndDepth(rootIds, childIds) {
    const counts = new Map();
    const depth = new Map();
    const order = [];
    for (const rootId of rootIds) {
        const stack = [{ id: rootId, visited: false }];
        depth.set(rootId, 0);
        while (stack.length > 0) {
            const frame = stack.pop();
            const children = childIds.get(frame.id) ?? [];
            if (!frame.visited) {
                order.push(frame.id);
                stack.push({ id: frame.id, visited: true });
                const nextDepth = (depth.get(frame.id) ?? 0) + 1;
                for (const child of children) {
                    if (!depth.has(child))
                        depth.set(child, nextDepth);
                    stack.push({ id: child, visited: false });
                }
            }
            else {
                let total = 0;
                for (const child of children)
                    total += 1 + (counts.get(child)?.totalReports ?? 0);
                counts.set(frame.id, { directReports: children.length, totalReports: total });
            }
        }
    }
    return { counts, depth, order };
}
/** Root -> person, inclusive of both ends. */
export function ancestorChain(model, personId) {
    const chain = [];
    const seen = new Set();
    let current = model.byId.get(personId);
    while (current && !seen.has(current.id)) {
        seen.add(current.id);
        chain.unshift(current);
        current = current.managerId ? model.byId.get(current.managerId) : undefined;
    }
    return chain;
}
/** Ids of everyone below `personId`, excluding them. */
export function descendantIds(model, personId) {
    const out = [];
    const stack = [...(model.childIds.get(personId) ?? [])];
    while (stack.length > 0) {
        const id = stack.pop();
        out.push(id);
        for (const child of model.childIds.get(id) ?? [])
            stack.push(child);
    }
    return out;
}
export function isDescendant(model, ancestorId, candidateId) {
    let current = model.byId.get(candidateId);
    const seen = new Set();
    while (current?.managerId) {
        if (current.managerId === ancestorId)
            return true;
        if (seen.has(current.managerId))
            return false;
        seen.add(current.managerId);
        current = model.byId.get(current.managerId);
    }
    return false;
}
/**
 * Can `personId` be re-parented under `newManagerId`? Returns a human-readable
 * reason when not, which the UI shows in the drop tooltip and the MCP server
 * returns verbatim to Claude.
 */
export function canReparent(model, personId, newManagerId) {
    const person = model.byId.get(personId);
    if (!person)
        return { ok: false, reason: `No person with id "${personId}".` };
    const manager = model.byId.get(newManagerId);
    if (!manager)
        return { ok: false, reason: `No person with id "${newManagerId}".` };
    if (personId === newManagerId)
        return { ok: false, reason: `${person.name} cannot report to themselves.` };
    if (person.managerId === newManagerId) {
        return { ok: false, reason: `${person.name} already reports to ${manager.name}.` };
    }
    const baseRoot = model.baseById.get(personId)?.managerId === null && !person.isNew;
    if (baseRoot) {
        return {
            ok: false,
            reason: `${person.name} is the top of the org chart and cannot be moved under someone else.`,
        };
    }
    if (isDescendant(model, personId, newManagerId)) {
        return {
            ok: false,
            reason: `${manager.name} reports up through ${person.name}, so this would create a reporting loop. Move ${manager.name} out from under ${person.name} first.`,
        };
    }
    return { ok: true };
}
/** Convenience: counts for a person, zeroed if unknown. */
export function countsFor(model, personId) {
    return model.counts.get(personId) ?? { directReports: 0, totalReports: 0 };
}
/** Shared fuzzy-ish search used by the UI command palette and the MCP server. */
export function searchPeople(model, rawQuery, limit = 25) {
    const query = rawQuery.trim().toLowerCase();
    if (query.length === 0)
        return [];
    const hits = [];
    for (const person of model.people) {
        const name = person.name.toLowerCase();
        const title = person.title.toLowerCase();
        const dept = (person.department ?? '').toLowerCase();
        const email = (person.email ?? '').toLowerCase();
        let score = 0;
        if (name === query)
            score = 100;
        else if (name.startsWith(query))
            score = 80;
        else if (name.split(/\s+/).some((part) => part.startsWith(query)))
            score = 70;
        else if (name.includes(query))
            score = 55;
        else if (email.startsWith(query))
            score = 50;
        else if (title.startsWith(query))
            score = 40;
        else if (title.includes(query))
            score = 30;
        else if (dept.includes(query))
            score = 20;
        else if (email.includes(query))
            score = 10;
        if (score > 0) {
            // Nudge senior people up so "director" style queries surface leaders first.
            score += Math.min(9, (model.counts.get(person.id)?.totalReports ?? 0) / 10);
            hits.push({ person, score });
        }
    }
    return hits.sort((a, b) => b.score - a.score || a.person.name.localeCompare(b.person.name)).slice(0, limit);
}
//# sourceMappingURL=tree.js.map